import React, {
  forwardRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import * as XLSX from "xlsx";
import { KeenIcon, LazyFormikSelect, DefaultTooltip, Alert } from "@/components";
import mapFieldService from "@/services/map-fields/mapFieldService";
import { FileDown } from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from '@/components/ui/skeleton'
import { normalizeFieldName, getApiErrorMessage } from '@/utils';

const CONTAINER_PADDING = 2;
const CONTAINER_W = 400;

const containerStyle = (title) => ({
  style: {
    background: "var(--tw-navbar-bg, #fff)",
    border: "1px solid rgba(0,0,0,.1)",
    borderRadius: 12,
    width: CONTAINER_W,
    padding: CONTAINER_PADDING,
  },
  data: { label: title },
  draggable: false,
  selectable: false,
});

// simple header node for containers
function ContainerNode({ data }) {
  return (
    <div className="relative w-full h-full bg-[--tw-navbar-bg] rounded-lg shadow-sm">
      <div
        className="w-full bg-gray-100 rounded-lg px-3 py-2 z-10"
        style={{ height: 40 }}
      >
        <span className="text-sm font-semibold text-gray-700">
          {data?.label}
        </span>
      </div>
    </div>
  );
}

// field pill node
function FieldNode({ id, data }) {
  const isUser = id.startsWith("usr-");
  const isSystem = id.startsWith("sys-");

  let nodeClass = "relative flex items-center justify-between w-full px-4 py-2 rounded-md shadow-sm border text-xs font-medium badge badge-outline";
  if (isSystem) {
    if (data?.isRequired) nodeClass += " badge-danger";
    else if (data?.isConditionalWarning) nodeClass += " badge-warning";
    else nodeClass += " badge-success";
  } else {
    nodeClass += " badge badge-primary";
  }

  const isSelected = data?.selectedNodeId === id;
  let isHighlightedNode = false;
  if (isSelected) {
    isHighlightedNode = true; // highlight style
  }

  return (
    <div
      className={nodeClass}
      style={{
        marginLeft: isUser ? "8px" : "auto",
        marginRight: isSystem ? "8px" : "auto",
        width: "100%",
        ...(isHighlightedNode && { border: '2px solid' })
      }}
    >
      {/* Source Handle for User fields */}
      {isUser && (
        <Handle
          type="source"
          position={Position.Bottom}
          id={`${id}-out`}
          className="!bg-primary !border-0"
          style={{
            width: 45,
            height: 5,
            right: -6,
            borderRadius: 3,
          }}
        />
      )}

      <span className="truncate font-semibold">{data?.label} {data?.isRequired && <span>*</span>}</span>

      {/* Target Handle for System fields */}
      {isSystem && (
        <Handle
          type="target"
          position={Position.Top}
          id={`${id}-in`}
          className="!bg-success !border-0"
          style={{
            width: 45,
            height: 5,
            left: '50%',
            borderRadius: 3,
          }}
        />
      )}
    </div>
  );
}

const nodeTypes = {
  container: ContainerNode,
  field: FieldNode,
};

const MapFieldModal = forwardRef(
  ({
    show,
    onClose,
    onConfirm,
    title,
    loading,
    previewMode = false,
    mapping,
    systemName,
    dataModuleType
  }, formikRef) => {
    if (!show) return null;

    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    const [systemFields, setSystemFields] = useState([]);
    const [userFields, setUserFields] = useState([{ id: 1, name: "" }]);
    const [requiredFields, setRequiredFields] = useState([]);
    const [conditionalFields, setConditionalFields] = useState({});
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [loadingSystemFields, setLoadingSystemFields] = useState(false);
    const [connectingNodeId, setConnectingNodeId] = useState(null);
    const [hasDuplicateColumns, setHasDuplicateColumns] = useState(false);

    const validationSchema = Yup.object().shape({
      system_name: Yup.string().required("source / System name is required"),
      type: Yup.string().required("Module type is required"),
    });

    const initialValues = { system_name: "", type: "" };

    useEffect(() => {
      if (previewMode && mapping) {
        try {
          const parsed = JSON.parse(mapping);

          const userFieldsSet = new Set(Object.values(parsed));
          const systemFieldsSet = new Set(Object.keys(parsed));

          const userFieldsArr = Array.from(userFieldsSet).map((name) => ({
            id: Date.now() + Math.random(),
            name,
          }));

          setUserFields(userFieldsArr);
          setSystemFields(Array.from(systemFieldsSet));

          // Build edges
          const edgeArr = Object.entries(parsed).map(([sys, usr]) => ({
            id: `e-${usr}-${sys}`,
            source: `usr-${usr}`,
            target: `sys-${sys}`,
            type: "bezier",
            animated: true,
            style: { stroke: "#2563eb", strokeWidth: 1, zIndex: 9999 },
          }));
          setEdges(edgeArr);
        } catch (err) {
          console.error("Failed to parse mapping JSON", err);
        }
      }
    }, [previewMode, mapping]);


    const rebuildNodes = useCallback(() => {
      const userNodes = userFields.filter(f => f.name).map((f, i) => ({
        id: `usr-${f.name}`,
        type: "field",
        parentNode: "container-user",
        extent: "parent",
        position: { x: 0, y: 50 + i * 48 },
        data: { label: f.name }
      }));

      const mapping = edges.reduce((acc, e) => {
        acc[e.target.replace("sys-", "")] = e.source.replace("usr-", "");
        return acc;
      }, {});

      const systemNodes = systemFields.map((s, i) => {
        const normalized = s;

        // Required if it's in requiredFields AND not yet mapped
        const isRequired = requiredFields.includes(normalized) &&
          !edges.some(e => e.target === `sys-${normalized}`);

        // Conditional warning if its pair is partially mapped
        const isConditionalWarning = Object.entries(conditionalFields).some(([a, b]) => {
          const mappedA = edges.some(e => e.target === `sys-${a}`);
          const mappedB = edges.some(e => e.target === `sys-${b}`);
          return (mappedA && !mappedB) || (!mappedA && mappedB);
        });

        return {
          id: `sys-${normalized}`,
          type: "field",
          parentNode: "container-system",
          extent: "parent",
          position: { x: 0, y: 50 + i * 48 },
          data: { label: normalized, isRequired, isConditionalWarning }
        };
      });

      setNodes([
        { id: "container-user", type: "container", position: { x: 50, y: 0 }, ...containerStyle("External Source Fields") },
        { id: "container-system", type: "container", position: { x: 700, y: 0 }, ...containerStyle("Our System Fields") },
        ...userNodes,
        ...systemNodes
      ]);

    }, [userFields, systemFields, edges, reactFlowInstance, requiredFields, conditionalFields]);

    useEffect(() => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView({ padding: 0.2 });
      }
    }, [reactFlowInstance]);

    // fetch system fields by module
    const getAllFields = async (moduleType) => {
      if (!moduleType) {
        setSystemFields([]);
        rebuildNodes(userFields, []);
        setEdges([]);
        return;
      }
      try {
        setLoadingSystemFields(true);
        const { data, status } = await mapFieldService.getAllExistingFields(moduleType);
        if (status) {
          setSystemFields(data.all_fields);
          setRequiredFields(data.required_fields || []);
          setConditionalFields(data.conditional_required_fields || {});
          rebuildNodes(userFields, data.all_fields);
          setEdges([]);
        }
      } catch (e) {
        console.error("Failed to fetch fields", e);
        toast.error("Could not load fields", {
          description: getApiErrorMessage(
            e,
            "An error occurred while fetching system fields. Please try again in a moment."
          ),
        });
      } finally {
        setLoadingSystemFields(false);
      }
    };

    // auto-scroll & focus to last user field
    useEffect(() => {
      if (userFields.length > 0) {
        const userContainer = document.getElementById("user-fields");
        const lastField = document.getElementById(
          `user-${userFields[userFields.length - 1].id}`
        );

        if (lastField) {
          lastField.focus();
        }

        if (userContainer) {
          userContainer.scrollTo({
            top: userContainer.scrollHeight,
            behavior: "smooth",
          });
        }
      }
    }, [userFields])

    // user field handlers
    const addUserField = () => {
      setUserFields((prev) => [...prev, { id: Date.now(), name: "" }]);
    };

    const updateUserField = (id, value) => {
      const normalized = normalizeFieldName(value);
      setUserFields((prev) =>
        prev.map((f) => (f.id === id ? { ...f, name: value.replace(" ", "_"), normalized } : f))
      );
    };

    const removeUserField = (id, name) => {
      setUserFields((prev) => prev.filter((f) => f.id !== id));
      // any edges leaving this user node should be removed
      setEdges((prev) => prev.filter((e) => e.source !== `usr-${name}`));
    };

    // when userFields change, rebuild nodes
    useEffect(() => {
      rebuildNodes(userFields, systemFields);
    }, [userFields, systemFields, rebuildNodes]);

    const isValidConnection = useCallback((conn) => {
      return conn.source?.startsWith("usr-") && conn.target?.startsWith("sys-");
    }, []);

    const onConnect = useCallback(
      (params) => {
        if (previewMode) return;
        setEdges((eds) => {
          const sourceUsed = eds.some((e) => e.source === params.source);
          const targetUsed = eds.some((e) => e.target === params.target);
          if (sourceUsed || targetUsed) return eds;
          return addEdge(
            {
              ...params,
              type: "bezier",
              animated: true,
              style: { stroke: "#2563eb", strokeWidth: 1, zIndex: 9999 },
            },
            eds
          );
        });
      },
      [previewMode]
    );

    const allRequiredMapped = requiredFields.every((field) =>
      edges.some((e) => e.target === `sys-${field}`)
    );

    const onEdgeClick = useCallback((evt, edge) => {
      if (previewMode) return;
      evt.stopPropagation();
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    }, [previewMode]);

    const onNodeClick = useCallback((event, node) => {
      if (previewMode) return;
      event.stopPropagation();

      const clickedId = node.id;
      const clickedIsUser = clickedId.startsWith("usr-");
      const clickedIsSystem = clickedId.startsWith("sys-");

      // STEP 1 — No node selected yet → store as first point
      if (!connectingNodeId) {
        setConnectingNodeId(clickedId);
        return;
      }

      // STEP 2 — Clicking same node → deselect
      if (connectingNodeId === clickedId) {
        setConnectingNodeId(null);
        return;
      }

      const startId = connectingNodeId;
      const startIsUser = startId.startsWith("usr-");
      const startIsSystem = startId.startsWith("sys-");

      // STEP 3 — ALLOWED COMBINATIONS ONLY:
      // user → system OR system → user
      const isValid =
        (startIsUser && clickedIsSystem) ||
        (startIsSystem && clickedIsUser);

      if (!isValid) {
        // invalid: user→user or system→system
        setConnectingNodeId(null);
        return;
      }

      // Determine correct direction
      const source = startIsUser ? startId : clickedId;
      const target = startIsUser ? clickedId : startId;

      // STEP 4 — Prevent duplicates
      setEdges((eds) => {
        const sourceUsed = eds.some((e) => e.source === source);
        const targetUsed = eds.some((e) => e.target === target);

        if (sourceUsed || targetUsed) {
          setConnectingNodeId(null);
          return eds;
        }

        return addEdge(
          {
            id: `${source}-${target}`,
            source,
            target,
            type: "bezier",
            animated: true,
            style: { stroke: "#2563eb", strokeWidth: 1, zIndex: 9999 },
          },
          eds
        );
      });

      setConnectingNodeId(null);
    }, [connectingNodeId, previewMode]);


    useEffect(() => {
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          data: {
            ...node.data,
            selectedNodeId: connectingNodeId, // pass the currently selected node
          },
        }))
      );
    }, [connectingNodeId]);

    const buildMappingPayload = () =>
      edges.reduce((acc, e) => {
        const systemField = e.target.replace("sys-", "");
        const userField = e.source.replace("usr-", "");
        acc[systemField] = userField;
        return acc;
      }, {});

    const handleSubmit = (values, formikHelpers) => {
      const { resetForm } = formikHelpers;
      const mapping = buildMappingPayload();

      if (Object.keys(mapping).length === 0) {
        toast.warning("Please map at least one field.");
        return;
      }
      // console.log('mapping', mapping);
      // return

      onConfirm({ ...values, mapping }, formikHelpers);
      // resetForm();
    };

    // import & replace user fields
    // const handleImportTemplate = (evt) => {
    //   const file = evt.target.files?.[0];
    //   if (!file) return;

    //   const reader = new FileReader();
    //   reader.onload = (e) => {
    //     try {
    //       const data = new Uint8Array(e.target.result);
    //       const wb = XLSX.read(data, { type: "array" });
    //       const sheet = wb.Sheets[wb.SheetNames[0]];
    //       const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    //       const headers = (rows?.[0] || [])
    //         .filter(Boolean)
    //         .map((h) => normalizeFieldName(String(h)));
    //       const uniqueHeaders = [...new Set(headers)];

    //       const imported = uniqueHeaders.map((h) => ({

    //         id: Date.now() + Math.random(),
    //         name: h,
    //       }));
    //       console.log('------->', imported);


    //       // replace fields + clear edges
    //       setUserFields(imported);
    //       setEdges([]);
    //     } catch (err) {
    //       console.error("Import failed", err);
    //     }
    //   };
    //   reader.readAsArrayBuffer(file);

    //   // allow re-upload of same file
    //   evt.target.value = "";
    // };

    const handleImportTemplate = (evt) => {
      const file = evt.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const wb = XLSX.read(data, { type: "array" });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

          const headers = (rows?.[0] || [])
            .filter(Boolean)
            .map((h) => normalizeFieldName(String(h)));

          // ----- Detect Duplicates -----
          const duplicates = headers.filter((h, i) => headers.indexOf(h) !== i);

          if (duplicates.length > 0) {
            setHasDuplicateColumns(true);

            toast.warning("Duplicate columns detected!", {
              description:
                "The file you uploaded contains duplicated columns. Please upload a file with unique column names.",
            });

            // DO NOT PROCEED — reject file
            evt.target.value = "";
            return;
          }

          // ----- No duplicates → proceed -----
          setHasDuplicateColumns(false);

          const uniqueHeaders = [...new Set(headers)];

          const imported = uniqueHeaders.map((h) => ({
            id: Date.now() + Math.random(),
            name: h,
          }));

          setUserFields(imported);
          setEdges([]);
        } catch (err) {
          console.error("Import failed", err);
        }
      };

      reader.readAsArrayBuffer(file);

      evt.target.value = "";
    };


    return (
      <div className="modal-wrapper">
        <div className="modal-body h-fit max-h-[85vh] w-11/12">
          <div className="sticky top-0 bg-[--tw-navbar-bg] dark:bg-[--tw-navbar-bg-dark] py-4">
            <h3 className="card-title mb-3">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              className="modal-close-btn top-3 right-3"
            >
              <KeenIcon icon="cross" />
            </button>
          </div>

          {!previewMode &&
            <Alert variant="info" className="w-fit text-start !p-1.5 !text-xs ml-auto" icon="information">
              <div className="!text-xs">You can also add custom fields in the system if they are mandatory for mapping.</div>
            </Alert>
          }

          {!previewMode ? (
            <Formik
              innerRef={formikRef}
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values, formikHelpers) => {
                handleSubmit(values, formikHelpers);
              }}
            >
              {({ setFieldValue, values }) => (
                <Form>
                  {/* top row */}
                  <div className="flex gap-4 mb-4">
                    <div className="w-full">
                      <DefaultTooltip title="type name of the source or system from which you are mapping fields." placement="right">
                        <label className="w-fit form-label mb-1 items-center gap-1">
                          <KeenIcon icon="information" className="size-3.5" />
                          <span>source / System Name</span>  <span className="text-red-500">*</span>
                        </label>
                      </DefaultTooltip>
                      <Field name="system_name" className="input" />
                      <ErrorMessage
                        name="system_name"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    <div className="w-full">
                      <label className="form-label mb-1">
                        Module / Data Type <span className="text-red-500">*</span>
                      </label>
                      <LazyFormikSelect
                        name="type"
                        value={
                          values.type
                            ? { value: values.type, label: values.type === "contact" ? "Contact" : "Matter" }
                            : null
                        }
                        options={[
                          { value: "contact", label: "Contact" },
                          { value: "matter", label: "Matter" },
                        ]}
                        placeholder="Select Module or Data Type"
                        onOptionSelect={(opt) => {
                          const v = opt?.value || "";
                          setFieldValue("type", v);
                          getAllFields(v);
                        }}
                        isClearable
                        onClear={() => {
                          setFieldValue("type", "");
                        }}
                      />
                      <ErrorMessage
                        name="type"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  </div>

                  {/* left list controls */}
                  <div className="grid grid-cols-3 gap-6">
                    <div className="col-span-1">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold">External Source Fields</h4>
                        <label className="btn btn-primary btn-sm btn-outline">
                          <input
                            type="file"
                            accept=".csv, .xlsx"
                            className="hidden"
                            onChange={handleImportTemplate}
                          />
                          <FileDown className="size-3.5" />
                          Import Template
                        </label>
                      </div>

                      <div id='user-fields' className="max-h-[45vh] overflow-auto">
                        {userFields.map((f) => (
                          <div key={f.id} className="flex items-center gap-2 mb-2">
                            <input
                              id={`user-${f.id}`}
                              className="input flex-1"
                              placeholder="Enter field name"
                              value={f.name}
                              onChange={(e) => updateUserField(f.id, e.target.value)}
                            />
                            {userFields.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeUserField(f.id, f.name)}
                                className="btn btn-danger btn-outline"
                                title="Remove field"
                              >
                                <KeenIcon icon="trash" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={addUserField}
                        className="btn btn-link mr-auto"
                      >
                        + Add Field
                      </button>
                    </div>

                    {/* react flow canvas 2 cols */}
                    <div className="col-span-2">
                      <div className="relative border rounded-lg h-[520px] overflow-hidden">
                        {loadingSystemFields ? (
                          <div className="absolute inset-0 flex flex-col gap-2 p-4 animate-pulse bg-gray-100">
                            {Array.from({ length: 10 }).map((_, i) => (
                              <Skeleton className="h-6" key={i} />
                            ))}
                          </div>
                        ) : (
                          <ReactFlow
                            nodeTypes={nodeTypes}
                            nodes={nodes}
                            edges={edges}
                            onConnect={onConnect}
                            onEdgeClick={onEdgeClick}
                            onNodeClick={onNodeClick}
                            isValidConnection={hasDuplicateColumns ? () => false : isValidConnection}
                            onInit={setReactFlowInstance}
                            fitView
                            proOptions={{ hideAttribution: true }}
                            className="bg-[--tw-navbar-bg] dark:bg-[--tw-navbar-bg-dark]"
                          >
                            <Background gap={16} />
                            <Controls
                              position="bottom-left"
                              showInteractive={false}
                              showFitView={true}
                              showZoom={true}
                              showZoomIn={true}
                              showZoomOut={true}
                              showPan={false}
                            />
                          </ReactFlow>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-4">
                    {(!allRequiredMapped) &&
                      (<Alert variant="danger" className="w-fit text-center mr-2 !p-1.5 !text-xs" icon="information">
                        <div className="!text-xs">You cannot submit until the mandatory fields are mapped.</div>
                      </Alert>
                      )}
                    <button
                      type="submit"
                      disabled={
                        loading ||
                        values.type === "" ||
                        values.system_name === "" ||
                        !allRequiredMapped
                      }
                      className="btn btn-primary">
                      {loading ? "Saving..." : "Save Source"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          ) : (
            //  Previe view
            <div className="flex flex-col gap-4">

              {/* NEW INFO HEADER */}
              <div className="flex justify-between items-center gap-4 mb-4">
                <div className="w-full">
                  <p className="form-label mb-1">
                    Source / System Name:
                  </p>
                  <input className="input" value={systemName} disabled />
                </div>

                <div className="w-full">
                  <p className="form-label mb-1">
                    Module Type:
                  </p>
                  <input className="input" value={dataModuleType} disabled />
                </div>
              </div>
              {/* END INFO HEADER */}

              <div className="grid grid-cols-3 gap-6">
                <div className="col-span-1">
                  <h4 className="font-semibold mb-2">External Source Fields</h4>
                  <div className="max-h-[55vh] overflow-auto">
                    {userFields.map((f) => (
                      <input
                        key={f.id}
                        type="text"
                        name="fields"
                        value={f.name}
                        disabled
                        className="input mb-2"
                      />
                    ))}
                  </div>
                </div>

                <div className="col-span-2">
                  <div className="relative border rounded-lg h-[520px] overflow-hidden">
                    <ReactFlow
                      nodeTypes={nodeTypes}
                      nodes={nodes}
                      edges={edges}
                      onInit={setReactFlowInstance}
                      fitView
                      proOptions={{ hideAttribution: true }}
                      className="bg-[--tw-navbar-bg] dark:bg-[--tw-navbar-bg-dark]"
                    >
                      <Background gap={16} />
                      <Controls
                        position="bottom-left"
                        showInteractive={false}
                        showFitView={true}
                        showZoom={true}
                        showZoomIn={true}
                        showZoomOut={true}
                        showPan={false}
                      />
                    </ReactFlow>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export { MapFieldModal };